import { MCP_URL } from '@/config';
import { getJwtToken } from '@/utils/self-hosted/jwt-manager';

// Client for the GrowthX MCP server's connections api (growthx/mcp, served at <MCP_URL>/api).
// It authenticates with the same dashboard JWT, and only ever returns the signed-in user's connections.

export type AiConnectionStatus = 'active' | 'revoked' | 'expired';

export type AiConnection = {
  id: string;
  clientName: string;
  createdAt: string;
  lastUsedAt: string | null;
  lastIp: string | null;
  lastUserAgent: string | null;
  approvedIp: string | null;
  approvedUserAgent: string | null;
  callCount: number;
  editCount: number;
  status: AiConnectionStatus;
  revokedAt: string | null;
};

export type AiConnectionEdit = {
  id: string;
  at: string;
  tool: string;
  environmentName: string;
  workflowId: string | null;
  stepId: string | null;
  layoutId: string | null;
  summary: string;
};

export type AiConnectionDetail = AiConnection & { recentEdits: AiConnectionEdit[] };

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${MCP_URL}/api${path}`, {
    ...init,
    headers: { Authorization: `Bearer ${getJwtToken() ?? ''}`, 'Content-Type': 'application/json', ...init.headers },
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Request failed (${res.status})`);
  }

  if (res.status === 204) return undefined as T;

  return ((await res.json()) as { data: T }).data;
}

export const getAiConnections = () => request<AiConnection[]>('/connections');

export const getAiConnection = (id: string) => request<AiConnectionDetail>(`/connections/${id}`);

export const revokeAiConnection = (id: string) => request<void>(`/connections/${id}/revoke`, { method: 'POST' });

export const deleteAiConnection = (id: string) => request<void>(`/connections/${id}`, { method: 'DELETE' });

export const revokeAllAiConnections = () => request<{ revoked: number }>('/connections/revoke-all', { method: 'POST' });
